import React, { useState, useRef, useEffect } from 'react';
import { 
  SpeakerWaveIcon, 
  PlayIcon, 
  StopIcon,
  Cog6ToothIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import PureWebAudioCalibration from '../components/PureWebAudioCalibration';

interface TinnitusProfile {
  frequency_hz: number;
  volume_normalized: number;
  method: 'manual' | 'auto';
}

const Assessment: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { user } = useUser();
  
  const [mode, setMode] = useState<'manual' | 'auto'>('manual');
  const [frequency, setFrequency] = useState(1000);
  const [volume, setVolume] = useState(0.3);
  const [panning, setPanning] = useState(0); // -1 (left) to 1 (right), 0 = center
  const [isPlaying, setIsPlaying] = useState(false);
  const [savedProfile, setSavedProfile] = useState<TinnitusProfile | null>(null);
  const [testProgress, setTestProgress] = useState(0);
  const [testResults, setTestResults] = useState<{ frequency: number; volume: number; panning: number } | null>(null);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [, setCurrentTestTone] = useState<{ frequency: number; volume: number } | null>(null);
  const [currentIteration, setCurrentIteration] = useState(0);
  const [isPlayingTone, setIsPlayingTone] = useState(false);
  const [, setIsPaused] = useState(false);
  const [, setUserResponses] = useState<Array<{freqHigher: boolean, volLouder: boolean}>>([]);
  const [pendingFreqResponse, setPendingFreqResponse] = useState<boolean | null>(null);
  const [pendingVolResponse, setPendingVolResponse] = useState<boolean | null>(null);
  const [pendingPanResponse, setPendingPanResponse] = useState<boolean | null>(null);
  const [, setCurrentPauseCount] = useState(0);
  const [isWaitingForInput, setIsWaitingForInput] = useState(false);
  
  // Volume calibration states
  const [calibratedMaxVolume, setCalibratedMaxVolume] = useState(0.3);
  const [calibratedMinVolume, setCalibratedMinVolume] = useState(0.01);
  
  // Play tone button state
  const [isPlayingResultTone, setIsPlayingResultTone] = useState(false);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const pannerNodeRef = useRef<StereoPannerNode | null>(null);

  // Initialize audio context
  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Update audio in real-time when frequency, volume, or panning changes
  useEffect(() => {
    if (isPlaying && oscillatorRef.current && gainNodeRef.current && pannerNodeRef.current && audioContextRef.current) {
      const audioContext = audioContextRef.current;
      const currentTime = audioContext.currentTime;
      
      // Update frequency
      oscillatorRef.current.frequency.setValueAtTime(frequency, currentTime);
      
      // Update volume
      gainNodeRef.current.gain.setValueAtTime(volume, currentTime);
      
      // Update panning
      pannerNodeRef.current.pan.setValueAtTime(panning, currentTime);
    }
  }, [frequency, volume, panning, isPlaying]);

  const startTone = () => {
    if (!audioContextRef.current) return;

    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const pannerNode = audioContext.createStereoPanner();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    pannerNode.pan.setValueAtTime(panning, audioContext.currentTime);

    oscillator.connect(gainNode);
    gainNode.connect(pannerNode);
    pannerNode.connect(audioContext.destination);

    oscillator.start();
    
    oscillatorRef.current = oscillator;
    gainNodeRef.current = gainNode;
    pannerNodeRef.current = pannerNode;
    setIsPlaying(true);
  };

  const stopTone = () => {
    if (oscillatorRef.current) {
      oscillatorRef.current.stop();
      oscillatorRef.current = null;
      gainNodeRef.current = null;
      pannerNodeRef.current = null;
      setIsPlaying(false);
    }
  };

  const saveProfile = () => {
    if (!user) {
      showError('Please sign in to save your profile', 'You need to be logged in to save tinnitus profiles.');
      return;
    }

    const profile: TinnitusProfile = {
      frequency_hz: frequency,
      volume_normalized: volume,
      method: 'manual'
    };
    setSavedProfile(profile);
    showSuccess('Profile Saved', `Tinnitus profile saved: ${formatFrequency(frequency)} at ${formatVolume(volume)}`);
    // In a real app, this would save to the backend
    console.log('Saved profile:', profile);
  };

  // Test parameters
  const [freqRange, setFreqRange] = useState({ min: 100, max: 12000 });
  const [volRange, setVolRange] = useState({ min: 0.01, max: 0.3 });
  const [currentFreq, setCurrentFreq] = useState(1000);
  const [currentVol, setCurrentVol] = useState(0.1);
  const [currentPan, setCurrentPan] = useState(0);
  const [testTimeout, setTestTimeout] = useState<NodeJS.Timeout | null>(null);

  const startAutomatedTest = () => {
    setTestProgress(0);
    setTestResults(null);
    setIsTestRunning(true);
    setCurrentIteration(0);
    setUserResponses([]);
    setIsPlayingTone(false);
    setIsPaused(false);
    setPendingFreqResponse(null);
    setPendingVolResponse(null);
    setPendingPanResponse(null);
    setCurrentPauseCount(0);
    setIsWaitingForInput(false);
    
    // Reset ranges to initial values
    setFreqRange({ min: 100, max: 12000 });
    setVolRange({ min: calibratedMinVolume, max: calibratedMaxVolume });
    setCurrentFreq(1000);  // Reset to initial medium frequency
    setCurrentVol(0.1);    // Reset to initial low volume
    setCurrentPan(0);      // Reset to center panning
    
    // Start first iteration
    startIteration(0);
  };

  const startIteration = (iteration: number) => {
    if (iteration >= 20) {
      // Test complete - calculate final results
      const finalFreq = currentFreq;
      const finalVol = currentVol;
      const finalPan = currentPan;
      setTestResults({ frequency: finalFreq, volume: finalVol, panning: finalPan });
      setTestProgress(100);
      setIsTestRunning(false);
      stopTone();
      return;
    }

    setCurrentIteration(iteration);
    setTestProgress((iteration / 20) * 100);
    setCurrentPauseCount(0);
    setIsWaitingForInput(true);
    
    // Start the play-pause cycle for this iteration
    playPauseCycle();
  };

  const playPauseCycle = (pauseCount: number = 0) => {
    // Play tone for 3 seconds (ALWAYS 3 seconds)
    playTestTone(currentFreq, currentVol, currentPan);
    setIsPlayingTone(true);
    setIsPaused(false);
    
    const playTimeout = setTimeout(() => {
      stopTone();
      setIsPlayingTone(false);
      
      // Pause duration increases: 1s, 2s, 3s, 4s, etc., but caps at 10s
      const pauseDuration = Math.min((pauseCount + 1) * 1000, 10000);
      setIsPaused(true);
      
      const pauseTimeout = setTimeout(() => {
        setIsPaused(false);
        // Continue the cycle with incremented pause count
        playPauseCycle(pauseCount + 1);
      }, pauseDuration);
      
      setTestTimeout(pauseTimeout);
    }, 3000); // ALWAYS 3 seconds play duration

    setTestTimeout(playTimeout);
  };

  const playTestTone = (freq: number, vol: number, pan: number = panning) => {
    if (!audioContextRef.current) return;

    // Stop any existing tone
    if (oscillatorRef.current) {
      oscillatorRef.current.stop();
    }

    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const pannerNode = audioContext.createStereoPanner();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
    gainNode.gain.setValueAtTime(vol, audioContext.currentTime);
    pannerNode.pan.setValueAtTime(pan, audioContext.currentTime);

    oscillator.connect(gainNode);
    gainNode.connect(pannerNode);
    pannerNode.connect(audioContext.destination);

    oscillator.start();
    
    oscillatorRef.current = oscillator;
    gainNodeRef.current = gainNode;
    pannerNodeRef.current = pannerNode;
    setCurrentTestTone({ frequency: freq, volume: vol });
  };

  const handleFrequencyResponse = (higher: boolean) => {
    if (!isTestRunning) return;
    setPendingFreqResponse(higher);
  };

  const handleVolumeResponse = (louder: boolean) => {
    if (!isTestRunning) return;
    setPendingVolResponse(louder);
  };

  const handlePanningResponse = (rightSide: boolean) => {
    if (!isTestRunning) return;
    setPendingPanResponse(rightSide);
  };

  const handleNextIteration = () => {
    if (pendingFreqResponse !== null && pendingVolResponse !== null && pendingPanResponse !== null) {
      processUserResponse(pendingFreqResponse, pendingVolResponse, pendingPanResponse);
    }
  };

  const processUserResponse = (freqHigher: boolean, volLouder: boolean, panRight: boolean) => {
    // Store user response
    setUserResponses(prev => [...prev, { freqHigher, volLouder, panRight }]);

    // Smart frequency range adjustment with direction shifting
    const freqAdjustment = (freqRange.max - freqRange.min) * 0.25; // 25% of range
    if (freqHigher) {
      // User wants higher frequency - shift range higher
      setFreqRange(prev => {
        const newMin = Math.max(100, prev.min + freqAdjustment);
        const newMax = Math.min(12000, prev.max + freqAdjustment * 0.5); // Expand slightly in new direction
        return { min: newMin, max: newMax };
      });
    } else {
      // User wants lower frequency - shift range lower
      setFreqRange(prev => {
        const newMin = Math.max(100, prev.min - freqAdjustment * 0.5); // Expand slightly in new direction
        const newMax = Math.min(12000, prev.max - freqAdjustment);
        return { min: newMin, max: newMax };
      });
    }

    // Smart volume range adjustment with direction shifting
    const volAdjustment = (volRange.max - volRange.min) * 0.25; // 25% of range
    if (volLouder) {
      // User wants louder volume - shift range louder
      setVolRange(prev => {
        const newMin = Math.max(calibratedMinVolume, prev.min + volAdjustment);
        const newMax = Math.min(calibratedMaxVolume, prev.max + volAdjustment * 0.5); // Expand slightly in new direction
        return { min: newMin, max: newMax };
      });
    } else {
      // User wants quieter volume - shift range quieter
      setVolRange(prev => {
        const newMin = Math.max(calibratedMinVolume, prev.min - volAdjustment * 0.5); // Expand slightly in new direction
        const newMax = Math.min(calibratedMaxVolume, prev.max - volAdjustment);
        return { min: newMin, max: newMax };
      });
    }

    // Calculate new test values with some randomness
    const freqRangeSize = freqRange.max - freqRange.min;
    const volRangeSize = volRange.max - volRange.min;
    
    const newFreq = freqRange.min + (freqRangeSize * 0.5) + (Math.random() - 0.5) * freqRangeSize * 0.2;
    const newVol = volRange.min + (volRangeSize * 0.5) + (Math.random() - 0.5) * volRangeSize * 0.2;

    setCurrentFreq(Math.max(100, Math.min(12000, newFreq)));
    setCurrentVol(Math.max(calibratedMinVolume, Math.min(calibratedMaxVolume, newVol)));

    // Smart panning adjustment
    if (panRight) {
      // User wants more right panning - move towards right
      setCurrentPan(Math.min(1, currentPan + 0.2));
    } else {
      // User wants more left panning - move towards left
      setCurrentPan(Math.max(-1, currentPan - 0.2));
    }

    // Reset pending responses
    setPendingFreqResponse(null);
    setPendingVolResponse(null);
    setPendingPanResponse(null);
    setIsWaitingForInput(false);

    // Clear any existing timeout
    if (testTimeout) {
      clearTimeout(testTimeout);
    }

    // Stop current tone and proceed to next iteration
    stopTone();
    startIteration(currentIteration + 1);
  };

  const stopAutomatedTest = () => {
    setIsTestRunning(false);
    setIsPlayingTone(false);
    setIsPaused(false);
    setCurrentTestTone(null);
    if (testTimeout) {
      clearTimeout(testTimeout);
    }
    stopTone();
  };

  const saveTestResults = () => {
    if (!user) {
      showError('Please sign in to save your results', 'You need to be logged in to save test results.');
      return;
    }

    if (testResults) {
      const profile: TinnitusProfile = {
        frequency_hz: testResults.frequency,
        volume_normalized: testResults.volume,
        method: 'auto'
      };
      setSavedProfile(profile);
      showSuccess('Test Results Saved', `Automated test results saved: ${formatFrequency(testResults.frequency)} at ${formatVolume(testResults.volume)}`);
      console.log('Saved test results:', profile);
    }
  };

  const formatFrequency = (freq: number) => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(1)} kHz`;
    }
    return `${freq} Hz`;
  };

  const formatVolume = (vol: number) => {
    // Convert to approximate dB scale (not scientifically accurate)
    const db = Math.round(20 * Math.log10(vol * 100));
    return `${db} dB`;
  };

  const formatPanning = (pan: number) => {
    if (pan === 0) return 'Center';
    if (pan < 0) return `Left ${Math.round(Math.abs(pan) * 100)}%`;
    return `Right ${Math.round(pan * 100)}%`;
  };


  // Toggle play/stop for result tone
  const toggleResultTone = () => {
    if (isPlayingResultTone) {
      stopTone();
      setIsPlayingResultTone(false);
    } else {
      if (testResults) {
        playTestTone(testResults.frequency, testResults.volume);
        setIsPlayingResultTone(true);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-auris-dark mb-4">
          Tinnitus Sound Assessment
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Use the manual mode to explore different frequencies and volumes, or try our automated test 
          to help identify your tinnitus characteristics.
        </p>
      </div>

      {/* Mode Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
              mode === 'manual'
                ? 'bg-auris-teal text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Cog6ToothIcon className="w-5 h-5 inline mr-2" />
            Manual Mode
          </button>
          <button
            onClick={() => setMode('auto')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
              mode === 'auto'
                ? 'bg-auris-teal text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SpeakerWaveIcon className="w-5 h-5 inline mr-2" />
            Automated Test
          </button>
        </div>

        {/* Manual Mode */}
        {mode === 'manual' && (
          <div className="space-y-6">
            {/* Pure Browser Audio Calibration Section */}
            <PureWebAudioCalibration 
              onCalibrationComplete={(result) => {
                if (result.finalVolume) {
                  // Update the calibrated volume range based on auto-calibration result
                  const newMaxVol = result.finalVolume / 100 * 0.8; // 80% of calibrated volume
                  const newMinVol = newMaxVol * 0.05; // 5% of max
                  setCalibratedMaxVolume(newMaxVol);
                  setCalibratedMinVolume(newMinVol);
                  setVolRange({ min: newMinVol, max: newMaxVol });
                }
              }}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Frequency Control */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frequency: {formatFrequency(frequency)}
                </label>
                <input
                  type="range"
                  min="100"
                  max="12000"
                  step="50"
                  value={frequency}
                  onChange={(e) => setFrequency(Number(e.target.value))}
                  className="w-full slider"
                  style={{ height: '8px' }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>100 Hz</span>
                  <span>12 kHz</span>
                </div>
              </div>

              {/* Volume Control */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume: {formatVolume(volume)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-full slider"
                  style={{ height: '8px' }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0 dB</span>
                  <span>40 dB</span>
                </div>
              </div>

              {/* Stereo Panning Control */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stereo Panning: {formatPanning(panning)}
                </label>
                <input
                  type="range"
                  min="-1"
                  max="1"
                  step="0.1"
                  value={panning}
                  onChange={(e) => setPanning(Number(e.target.value))}
                  className="w-full slider"
                  style={{ height: '8px' }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Left Ear</span>
                  <span>Center</span>
                  <span>Right Ear</span>
                </div>
              </div>
            </div>

            {/* Playback Controls */}
            <div className="flex justify-center space-x-4">
              {!isPlaying ? (
                <button
                  onClick={startTone}
                  className="flex items-center space-x-2 bg-auris-teal text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                >
                  <PlayIcon className="w-5 h-5" />
                  <span>Play Tone</span>
                </button>
              ) : (
                <button
                  onClick={stopTone}
                  className="flex items-center space-x-2 bg-red-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                >
                  <StopIcon className="w-5 h-5" />
                  <span>Stop Tone</span>
                </button>
              )}
            </div>

            {/* Save Profile */}
            <div className="text-center">
              <button
                onClick={saveProfile}
                disabled={!user}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors mx-auto ${
                  user
                    ? 'bg-auris-blue text-auris-dark hover:bg-opacity-80'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <CheckCircleIcon className="w-5 h-5" />
                <span>Save Tinnitus Profile</span>
              </button>
              {!user && (
                <p className="text-xs text-gray-400 mt-2">Sign in to save your profile</p>
              )}
            </div>
          </div>
        )}

        {/* Automated Test Mode */}
        {mode === 'auto' && (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                The automated test will play tones for 3 seconds, then pause. During the pause, 
                tell us if the tone needs to be higher/lower and louder/quieter than your tinnitus.
                The test runs for 20 iterations to find the perfect match.
              </p>
              
              {!isTestRunning && testProgress === 0 && (
                <button
                  onClick={startAutomatedTest}
                  className="bg-auris-teal text-white px-8 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                >
                  Start Automated Test
                </button>
              )}

              {isTestRunning && (
                <div className="space-y-6">
                  <div className="bg-auris-blue bg-opacity-20 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-auris-dark mb-4">
                      Iteration {currentIteration + 1} of 20
                    </h3>
                    
                    {/* Debug Information */}
                    <div className="bg-gray-100 rounded-lg p-3 mb-4 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="font-medium text-gray-700">Current Values:</div>
                          <div className="text-auris-teal font-mono">
                            {formatFrequency(currentFreq)} / {formatVolume(currentVol)} / {formatPanning(currentPan)}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-gray-700">Range:</div>
                          <div className="text-gray-600 font-mono text-xs">
                            {formatFrequency(freqRange.min)} - {formatFrequency(freqRange.max)}
                          </div>
                          <div className="text-gray-600 font-mono text-xs">
                            {formatVolume(volRange.min)} - {formatVolume(volRange.max)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                      <div 
                        className="bg-auris-teal h-2 rounded-full transition-all duration-300"
                        style={{ width: `${testProgress}%` }}
                      ></div>
                    </div>
                    
                    {isWaitingForInput && (
                      <div className="text-center">
                        {isPlayingTone ? (
                          <>
                            <div className="text-2xl mb-2">🔊</div>
                            <p className="text-sm text-gray-600 mb-4">
                              Playing tone... Listen carefully and compare with your tinnitus
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="text-2xl mb-2">⏸️</div>
                            <p className="text-sm text-gray-600 mb-4">
                              Pause... Think about the comparison. Was the tone higher or lower, louder or quieter than your tinnitus?
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    {isWaitingForInput && (
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Frequency: {pendingFreqResponse !== null ? (pendingFreqResponse ? 'Higher ✓' : 'Lower ✓') : 'Not selected'}
                          </p>
                          <div className="flex justify-center space-x-4">
                            <button
                              onClick={() => handleFrequencyResponse(false)}
                              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                pendingFreqResponse === false 
                                  ? 'bg-red-200 text-red-800 border-2 border-red-400' 
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              Lower
                            </button>
                            <button
                              onClick={() => handleFrequencyResponse(true)}
                              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                pendingFreqResponse === true 
                                  ? 'bg-blue-200 text-blue-800 border-2 border-blue-400' 
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                            >
                              Higher
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Volume: {pendingVolResponse !== null ? (pendingVolResponse ? 'Louder ✓' : 'Quieter ✓') : 'Not selected'}
                          </p>
                          <div className="flex justify-center space-x-4">
                            <button
                              onClick={() => handleVolumeResponse(false)}
                              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                pendingVolResponse === false 
                                  ? 'bg-orange-200 text-orange-800 border-2 border-orange-400' 
                                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              }`}
                            >
                              Quieter
                            </button>
                            <button
                              onClick={() => handleVolumeResponse(true)}
                              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                pendingVolResponse === true 
                                  ? 'bg-green-200 text-green-800 border-2 border-green-400' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              Louder
                            </button>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Stereo Position: {pendingPanResponse !== null ? (pendingPanResponse ? 'More Right ✓' : 'More Left ✓') : 'Not selected'}
                          </p>
                          <div className="flex justify-center space-x-4">
                            <button
                              onClick={() => handlePanningResponse(false)}
                              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                pendingPanResponse === false 
                                  ? 'bg-purple-200 text-purple-800 border-2 border-purple-400' 
                                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              }`}
                            >
                              More Left
                            </button>
                            <button
                              onClick={() => handlePanningResponse(true)}
                              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                                pendingPanResponse === true 
                                  ? 'bg-indigo-200 text-indigo-800 border-2 border-indigo-400' 
                                  : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                              }`}
                            >
                              More Right
                            </button>
                          </div>
                        </div>

                        {pendingFreqResponse !== null && pendingVolResponse !== null && pendingPanResponse !== null && (
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-medium mb-4">
                              ✓ All responses received! Ready to proceed to next iteration.
                            </p>
                            <button
                              onClick={handleNextIteration}
                              className="bg-auris-teal text-white px-8 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                            >
                              Next Iteration
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-4 text-xs text-gray-500 text-center">
                      You can click the buttons anytime during the iteration to provide feedback
                    </div>

                    <button
                      onClick={stopAutomatedTest}
                      className="mt-4 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                    >
                      Stop Test
                    </button>
                  </div>
                </div>
              )}

              {testResults && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    Test Complete!
                  </h3>
                  <p className="text-green-700 mb-4">
                    After 20 iterations, we've identified your tinnitus characteristics:
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-700">
                        {formatFrequency(testResults.frequency)}
                      </div>
                      <div className="text-sm text-green-600">Frequency</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-700">
                        {formatVolume(testResults.volume)}
                      </div>
                      <div className="text-sm text-green-600">Volume</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-700">
                        {formatPanning(testResults.panning)}
                      </div>
                      <div className="text-sm text-green-600">Stereo Position</div>
                    </div>
                  </div>
                  <div className="flex justify-center space-x-4 mt-4 flex-wrap gap-y-2">
                    <div className="flex flex-col items-center">
                      <button
                        onClick={saveTestResults}
                        disabled={!user}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          user
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Save Results
                      </button>
                      {!user && (
                        <p className="text-xs text-gray-400 mt-1">Sign in to save</p>
                      )}
                    </div>
                    <button
                      onClick={toggleResultTone}
                      className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                        isPlayingResultTone 
                          ? 'bg-red-500 text-white hover:bg-red-600' 
                          : 'bg-auris-teal text-white hover:bg-auris-teal-dark'
                      }`}
                    >
                      {isPlayingResultTone ? 'Stop Tone' : 'Play Tone'}
                    </button>
                    <button
                      onClick={() => {
                        setTestResults(null);
                        setTestProgress(0);
                        // Reset to initial values for new test
                        setFreqRange({ min: 100, max: 12000 });
                        setVolRange({ min: calibratedMinVolume, max: calibratedMaxVolume });
                        setCurrentFreq(1000);
                        setCurrentVol(0.1);
                        setCurrentPan(0);
                        setUserResponses([]);
                        setPendingFreqResponse(null);
                        setPendingVolResponse(null);
                        setPendingPanResponse(null);
                        // Stop any playing tone and reset state
                        stopTone();
                        setIsPlayingResultTone(false);
                      }}
                      className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                    >
                      Test Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Saved Profile Display */}
      {savedProfile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">
            Profile Saved Successfully
          </h3>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-xl font-bold text-green-700">
                {formatFrequency(savedProfile.frequency_hz)}
              </div>
              <div className="text-sm text-green-600">Frequency</div>
            </div>
            <div>
              <div className="text-xl font-bold text-green-700">
                {formatVolume(savedProfile.volume_normalized)}
              </div>
              <div className="text-sm text-green-600">Volume</div>
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">
            Method: {savedProfile.method === 'manual' ? 'Manual exploration' : 'Automated test'}
          </p>
        </div>
      )}

      {/* Warning Notice */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Important:</strong> This tool is not medically calibrated and results are approximate. 
          Use this information as a starting point for discussions with your healthcare provider.
        </p>
      </div>
    </div>
  );
};

export default Assessment;
